import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';

/**
 * The issue-reporting surfaces' ACTION buttons — `Later` and `Discard`, wherever the
 * two of them appear.
 *
 * ## Why these live in one module
 *
 * `Discard` exists on three surfaces — the flag panel's header, the Issue button's
 * backlog popover, and the task-router lobby card — and it does the same thing on all
 * three: it voids the report. It had drifted into two different treatments (a solid
 * button in the panel, a ghost outline on the rows), which is the same word for the
 * same effect asking to be read two different ways. Declaring it once is the only way
 * that stays fixed.
 *
 * ## Why they wear the ACTION BAR's colours
 *
 * Straight from `intentButtonStyle`'s HITL3 sections:
 *
 * - `Later` is the Conversation amber (`Colors.acknowledge`, near-black ink) — the
 *   Welcome / Okay / Say Again / Moment treatment.
 * - `Discard` is the Escalation red (`Colors.requestStaff`, white ink) — the Staff
 *   treatment.
 *
 * Borrowed rather than invented because these are ACTIONS, and the bar is where this
 * console keeps its action language: an agent who has pressed Welcome ten thousand
 * times reads an amber pill as a button before they have read the word on it. Both
 * shipped as translucent white on daintree — a 14% fill and a 32% border — and
 * disappeared into the surfaces they sat on, so an agent looking for the way out found
 * neither. It also separates them from the 23 pastel chips below, which are values,
 * not actions.
 *
 * This is the OPPOSITE of `issueChipStyle`'s rule 1, and deliberately so. That rule
 * keeps the bar's amber / blue / red off the CHIPS, because a value wearing an action's
 * colour looks like something that will happen when you press it. The same colours on
 * something that really is an action carry exactly the meaning they already have.
 * `Colors.red` still stays out: pure `#FF0000` is barely legible on daintree.
 *
 * The badge and the backlog cards' edge keep `Colors.amaranth`, the console's error
 * tone, because those are STATUS marks rather than things you press. Actions borrow
 * the action palette; state does not.
 *
 * ## Known costs, accepted
 *
 * 1. Amber is also the dial's PAUSED tone, in the panel's same header row. The two
 *    agree more than they collide — a held clock and "deal with this later" are the
 *    same idea — but the pause signal is deliberately minimal (numeral and glyph), and
 *    an amber pill beside it dilutes it.
 * 2. `Discard` is now as inviting as the safe exit, and it has NO undo: the retraction
 *    is emitted on press and the frozen context goes with it. On the backlog surfaces
 *    that repeats per row, so a list of five reports is five red buttons next to five
 *    rows whose actual purpose is to be opened and classified. Findability won the
 *    trade — an exit an agent cannot find is its own failure, and they were letting the
 *    window time out instead — but accidental discards are the thing to watch for
 *    first in QA.
 */
const ISSUE_ACTION_BUTTON_SX = {
  all: 'unset',
  cursor: 'pointer',
  flexShrink: 0,
  px: '10px',
  py: '5px',
  // 700 and 4px both match the bar's pills, rather than sitting one notch lighter
  // than the thing they are quoting.
  borderRadius: '4px',
  fontSize: HITL3_FONT_SIZE,
  fontWeight: 700,
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
  // `all: unset` resets `outline`, so without this the buttons take keyboard focus
  // invisibly — the same trap the chips hit. `:focus-visible` keeps the ring off
  // mouse presses, and the 2px offset puts it on the daintree ground rather than on
  // the button's own fill, where a white ring on amber would be invisible.
  '&:focus-visible': {
    outline: `2px solid ${Colors.white}`,
    outlineOffset: '2px',
  },
} as const;

/**
 * Voids the report. The Escalation group's exact pair — the Staff pill's treatment.
 *
 * NO hover shift, here and below, because the bar's pills have none either: they hold
 * one solid colour. A control that behaves differently from the thing it is imitating
 * is worse than one that looks different.
 */
export const DISCARD_BUTTON_SX = {
  ...ISSUE_ACTION_BUTTON_SX,
  backgroundColor: Colors.requestStaff,
  color: Colors.white,
} as const;

/**
 * Closes the flag window and leaves the report outstanding. The Conversation group's
 * exact pair.
 *
 * Panel-only: the backlog surfaces have no window to defer, and a row already closes
 * by being clicked again, by Escape or by a click away.
 */
export const LATER_BUTTON_SX = {
  ...ISSUE_ACTION_BUTTON_SX,
  backgroundColor: Colors.acknowledge,
  color: Colors.primaryButtonTextColor,
} as const;
